import { Component } from 'react';
import PropTypes from 'prop-types';
import { graphql, createFragmentContainer } from 'react-relay/legacy';
import HeartWrapper from 'dibs-portfolio-heart';
import { ItemTile } from 'dibs-search-product-tile/exports/ItemTile';
import authModalLoader from 'dibs-buyer-layout/src/utils/AuthModalLoader';
import {
    trackEvent,
    eventNameConstants,
    interactionTypeConstants,
    stepInteractionConstants,
} from 'dibs-tracking';
import FavoritesItemProvider from 'dibs-portfolio-folder/exports/FavoritesItemProvider';

import FolderWrapperWrapper from '../sharedComponents/FolderWrapperWrapper';
import { getFolderTrackingHooks, locations } from '../utils/folderTracking';
import { ServerVarsContextConsumer } from '../sharedComponents/ServerVarsContext';

// Styles
import styles from './styles/ShoppingWithTile.scss';

const { EVENT_WISHLIST } = eventNameConstants;
const { INTERACTION_TYPE_ITEM_FAVORITING } = interactionTypeConstants;
const { STEP_INTERACTION_ITEM_ADDED, STEP_INTERACTION_ITEM_REMOVED } = stepInteractionConstants;

class ShoppingWithTileComponent extends Component {
    constructor(props) {
        super(props);

        this.authModalShow = this.authModalShow.bind(this);
    }

    authModalShow(favoriteItem) {
        if (!this.props.hasUserId) {
            authModalLoader
                .show({
                    flow: authModalLoader.constants.SAVE_ITEM_FLOW,
                    ga: {
                        label: 'save item - shopping with page',
                    },
                })
                .then(favoriteItem);
        }
    }

    trackFavoriting(action, itemId, step_interaction_name) {
        trackEvent(
            {
                action,
                label: 'shopping with',
                category: 'item favoriting',
                isInteractiveEvent: true,
                additional: itemId ? { itemId } : {},
                eventName: EVENT_WISHLIST,
                interaction_type: INTERACTION_TYPE_ITEM_FAVORITING,
                step_interaction_name,
            },
            null
        );
    }

    render() {
        const {
            currency,
            hasUserId,
            viewer,
            index,
            item,
            onContentLoaded,
            selectedItemIds,
            shouldFillInFolder,
            loadPortfolioData,
            trackProductClick,
            userId,
            activeQuickView,
            openQuickView,
            closeQuickView,
            isClient,
        } = this.props;

        const getFavoritesComponent = ({
            isMobile,
            isFolderVisible,
            showFolderPlusIcon,
            onFavorited,
            onUnFavorited,
            onFolderActionStart,
            onFolderActionEnd,
        }) => (
            <div className={styles.iconWrapper}>
                <div className={styles.heart}>
                    <HeartWrapper
                        authModalShow={this.authModalShow}
                        itemId={item.serviceId}
                        loadPortfolioData={loadPortfolioData}
                        onFavorited={() => {
                            this.trackFavoriting(
                                'item added',
                                item.serviceId,
                                STEP_INTERACTION_ITEM_ADDED
                            );
                            onFavorited?.();
                        }}
                        onUnFavorited={() => {
                            this.trackFavoriting(
                                'item removed',
                                item.serviceId,
                                STEP_INTERACTION_ITEM_REMOVED
                            );
                            onUnFavorited?.();
                        }}
                        selectedItemIds={selectedItemIds}
                        userId={hasUserId ? userId : null}
                        userIds={hasUserId ? [userId] : []}
                        viewer={viewer ? viewer : null}
                        theme="dark"
                    />
                </div>
                {!isMobile && isFolderVisible && (
                    <div className={styles.folder}>
                        <FolderWrapperWrapper
                            authModalTrackingLabel="save item - similar items"
                            primaryItemPk={item.serviceId}
                            selectedItemIds={selectedItemIds}
                            selectedItemImages={
                                item.firstPhotoWebPath ? [item.firstPhotoWebPath] : []
                            }
                            selectedItemTitles={item.title ? [item.title] : []}
                            shouldFillInFolder={shouldFillInFolder}
                            singlePortfolioItem
                            trackingHooks={getFolderTrackingHooks(locations.VISUALLY_SIMILAR)}
                            userId={hasUserId ? userId : null}
                            userIds={hasUserId ? [userId] : []}
                            viewer={viewer ? viewer : null}
                            theme="dark"
                            showPlusIcon={showFolderPlusIcon}
                            onFolderSave={onFolderActionStart}
                            onFolderRemove={onFolderActionStart}
                            onNewFolderSave={onFolderActionStart}
                            onRemoveItemSuccess={onFolderActionEnd}
                        />
                    </div>
                )}
            </div>
        );
        return (
            <ServerVarsContextConsumer>
                {({ isMobile }) => (
                    <FavoritesItemProvider
                        item={item}
                        user={null}
                        userId={userId}
                        applyMouseEvents={!isMobile}
                    >
                        {favoritesItemProps => (
                            <ItemTile
                                currency={currency}
                                index={index}
                                isMobile={isMobile}
                                item={item}
                                itemSearch={null}
                                itemsPerRow={3}
                                onClick={() => trackProductClick(item)}
                                onContentLoaded={onContentLoaded}
                                showSellerName={false}
                                showQuickView
                                activeQuickView={activeQuickView}
                                openQuickView={openQuickView}
                                closeQuickView={closeQuickView}
                                useLoFiLazyLoader
                                viewer={null}
                                showMSKUSwatch={false}
                                showMeasurements={false}
                                showPrice={isClient}
                                favoritesComponent={getFavoritesComponent({
                                    isMobile,
                                    ...favoritesItemProps,
                                })}
                            />
                        )}
                    </FavoritesItemProvider>
                )}
            </ServerVarsContextConsumer>
        );
    }
}

ShoppingWithTileComponent.propTypes = {
    currency: PropTypes.string,
    deviceType: PropTypes.string,
    hasUserId: PropTypes.bool,
    viewer: PropTypes.object,
    index: PropTypes.number,
    item: PropTypes.object.isRequired,
    activeQuickView: PropTypes.number,
    openQuickView: PropTypes.func.isRequired,
    closeQuickView: PropTypes.func.isRequired,
    onContentLoaded: PropTypes.func,
    selectedItemIds: PropTypes.array,
    shouldFillInFolder: PropTypes.bool,
    loadPortfolioData: PropTypes.bool,
    trackProductClick: PropTypes.func,
    userId: PropTypes.string,
    isClient: PropTypes.bool,
};

ShoppingWithTileComponent.defaultProps = {
    onContentLoaded: () => {},
    trackProductClick: () => {},
};

export const ShoppingWithTile = createFragmentContainer(ShoppingWithTileComponent, {
    item: graphql`
        fragment ShoppingWithTile_item on Item {
            categoryPath
            ecommerceTrackingParams
            firstPhotoWebPath(size: small)
            serviceId
            title
            ...FavoritesItemProvider_item
            ...ItemTile_item @arguments(isTrade: $isTrade, userCountryCode: $userCountryCode)
        }
    `,
});
